/** @file Which view the Inspector shows: focused entity, else keys, else vertices, else a bone or a slot. */
import { ENTITY_KINDS } from '@pose2d/format/doc/entityKinds';
import type { Doc, EntityKind, Id } from '@pose2d/format/types';
import type { InspectorFocus } from '../../stores/focusStore';
import type { KeyRef, SelectionSlice } from '../../stores/types';

/** What the Inspector edits. */
export type InspectorTarget =
  | { kind: 'entity'; entity: EntityKind; id: Id }
  | { kind: 'keys'; keys: readonly KeyRef[] }
  | { kind: 'vertices'; attachment: Id; vertices: readonly number[] }
  | { kind: 'project' };

const exists = (doc: Doc, kind: EntityKind, id: Id): boolean => Object.hasOwn(doc[ENTITY_KINDS[kind]], id);

/** Priority: a live focus, keys, vertices of the first selected mesh, the last live bone, the last live slot. */
export function inspectorTarget(selection: SelectionSlice, focus: InspectorFocus | null, doc: Doc): InspectorTarget {
  if (focus !== null && exists(doc, focus.kind, focus.id)) return { kind: 'entity', entity: focus.kind, id: focus.id };
  if (selection.keys.length > 0) return { kind: 'keys', keys: selection.keys };
  const mesh = selection.vertices[0]?.[0];
  if (mesh !== undefined) return { kind: 'vertices', attachment: mesh, vertices: selection.vertices.filter(([a]) => a === mesh).map(([, v]) => v) };
  const bone = selection.bones.findLast((id) => exists(doc, 'bone', id));
  if (bone !== undefined) return { kind: 'entity', entity: 'bone', id: bone };
  const slot = selection.slots.findLast((id) => exists(doc, 'slot', id));
  return slot === undefined ? { kind: 'project' } : { kind: 'entity', entity: 'slot', id: slot };
}
